import { tr } from '@/contexts/LanguageContext';
/**
 * mediaPreview — helper ÚNICO y robusto de previsualización de medios.
 *
 * Sprint 5 · P0-1/P0-2 (Android): los previews quedaban en negro/vacío porque
 *   1. Samsung/Google Photos entregan HEIC/HEIF — Chrome Android NO lo
 *      decodifica en <img>, así que el blob URL "carga" pero pinta negro.
 *   2. Archivos content:// llegan con MIME vacío → rechazo erróneo.
 *   3. Nadie escuchaba onerror → fallo 100% silencioso.
 *
 * Este módulo garantiza que TODO objectURL entregado a la UI es decodificable
 * por el navegador actual, convirtiendo HEIC→JPEG bajo demanda (heic2any,
 * import dinámico — no engorda el bundle si no hace falta).
 *
 * API:
 *   prepareImagePreview(file) → { url, file, converted }
 *     `file` es el archivo A SUBIR (JPEG convertido si el original era HEIC,
 *     para que el resto de usuarios también puedan verlo).
 *   prepareVideoPreview(file) → { url, file }
 *     Valida que el navegador puede leer metadata del video.
 *   Ambas lanzan MediaPreviewError con mensaje claro y recuperable.
 */

export class MediaPreviewError extends Error {
  constructor(message) {
    super(message);
    this.name = 'MediaPreviewError';
    this.recoverable = true;
  }
}

const _ext = (file) => {
  const name = (file?.name || '').toLowerCase();
  return name.includes('.') ? name.split('.').pop() : '';
};

const _isHeic = (file) => {
  const t = (file?.type || '').toLowerCase();
  if (t === 'image/heic' || t === 'image/heif') return true;
  const e = _ext(file);
  return e === 'heic' || e === 'heif';
};

/** Prueba real de decodificación: si el navegador no puede pintar la imagen,
 *  Image.decode()/onerror lo dice en vez de dejar un recuadro negro. */
const _probeImage = (url, timeoutMs = 10000) => new Promise((resolve, reject) => {
  const img = new Image();
  const timer = setTimeout(() => {
    img.src = '';
    reject(new MediaPreviewError(tr("La imagen tardó demasiado en cargar.")));
  }, timeoutMs);
  img.onload = () => {
    clearTimeout(timer);
    if (!img.naturalWidth || !img.naturalHeight) {
      reject(new MediaPreviewError(tr("La imagen no tiene dimensiones válidas.")));
      return;
    }
    resolve({ width: img.naturalWidth, height: img.naturalHeight });
  };
  img.onerror = () => {
    clearTimeout(timer);
    reject(new MediaPreviewError(tr("El navegador no pudo decodificar la imagen.")));
  };
  img.src = url;
});

const _convertHeicToJpeg = async (file) => {
  let heic2any;
  try {
    heic2any = (await import('heic2any')).default;
  } catch {
    throw new MediaPreviewError(
      tr("Tu foto está en formato HEIC y no pudimos convertirla. Elige una foto JPG o PNG."),
    );
  }
  try {
    const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    const blob = Array.isArray(out) ? out[0] : out;
    const base = (file.name || 'foto').replace(/\.[^.]+$/, '');
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
  } catch {
    throw new MediaPreviewError(
      tr("No pudimos convertir esta foto HEIC. Prueba exportarla como JPG o elige otra."),
    );
  }
};

export const prepareImagePreview = async (file) => {
  if (!file) throw new MediaPreviewError(tr("Selecciona una imagen."));
  let workFile = file;
  let converted = false;

  // HEIC/HEIF: convertir ANTES de intentar el preview — Chrome/Samsung
  // Internet en Android no lo decodifican de forma nativa.
  if (_isHeic(file)) {
    workFile = await _convertHeicToJpeg(file);
    converted = true;
  }

  let url = URL.createObjectURL(workFile);
  try {
    await _probeImage(url);
    return { url, file: workFile, converted };
  } catch (err) {
    URL.revokeObjectURL(url);
    // Último intento: quizá era HEIC con MIME/extensión mentirosos.
    if (!converted) {
      try {
        workFile = await _convertHeicToJpeg(file);
        url = URL.createObjectURL(workFile);
        await _probeImage(url);
        return { url, file: workFile, converted: true };
      } catch {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
      }
    }
    throw err instanceof MediaPreviewError
      ? err
      : new MediaPreviewError(tr("No pudimos leer esta imagen. Prueba con otra foto (JPG o PNG)."));
  }
};

/** Valida que el navegador puede leer metadata (dimensiones/duración) del
 *  video antes de mostrar el preview. */
export const prepareVideoPreview = (file, timeoutMs = 15000) => new Promise((resolve, reject) => {
  if (!file) {
    reject(new MediaPreviewError(tr("Selecciona un video.")));
    return;
  }
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  const cleanup = () => {
    video.removeAttribute('src');
    try { video.load(); } catch { /* noop */ }
  };
  const timer = setTimeout(() => {
    cleanup();
    URL.revokeObjectURL(url);
    reject(new MediaPreviewError(tr("El video tardó demasiado en prepararse. Intenta de nuevo.")));
  }, timeoutMs);
  video.onloadedmetadata = () => {
    clearTimeout(timer);
    const meta = {
      duration: video.duration,
      width: video.videoWidth,
      height: video.videoHeight,
    };
    cleanup();
    resolve({ url, file, meta });
  };
  video.onerror = () => {
    clearTimeout(timer);
    cleanup();
    URL.revokeObjectURL(url);
    reject(new MediaPreviewError(
      tr("Tu navegador no puede reproducir este video. Usa MP4 (H.264) o elige otro archivo."),
    ));
  };
  video.src = url;
});
